import React from "react";
import {
  Box,
  createStyles,
  debounce,
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Zoom,
} from "@material-ui/core";

import { Search as SearchIcon } from "@eroad/icons/material";

import type { SearchOption } from "./types";

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    search: {
      width: "100%",
      "& > div": {
        width: "100%",
        paddingRight: "5px",
      },
      "& > input": {
        fontSize: "14px",
        fontWeight: "500",
      },
      "& button": {
        padding: 0,
      },
    },
    adornedEnd: {
      paddingRight: 0,
      "& button": {
        padding: 0,
      },
    },
    select: {
      "& svg": {
        top: "calc(50% - 14px)",
      },
    },
  }),
);

export interface ButtonCommand {
  title: string;
  icon: React.ReactElement;
  onAction: () => Promise<void>;
}

/** Can have up to two filters with commands */
export interface SearchToolbarProps {
  /** Search query to set */
  query?: string;
  queryPlaceholderText?: string;
  onChangeQuery: (query: string) => void;
  /** primary filter dropdown */
  optionsPrimary?: SearchOption[];
  selectedOptionPrimary?: string;
  onChangeOptionPrimary?: (option: string) => void;
  /** secondary filter dropdown */
  optionsSecondary?: SearchOption[];
  selectedOptionSecondary?: string;
  onChangeOptionSecondary?: (option: string) => void;
  /** Toolbar commands with actions */
  commands: ButtonCommand[];
}

const SearchToolbar = ({
  query,
  queryPlaceholderText,
  onChangeQuery,
  optionsPrimary,
  selectedOptionPrimary,
  onChangeOptionPrimary,
  optionsSecondary,
  selectedOptionSecondary,
  onChangeOptionSecondary,
  commands,
}: SearchToolbarProps): JSX.Element => {
  const classes = useStyles();
  const [search, setSearch] = React.useState<string>(query ?? "");
  const [optionSelect, setOptionSelect] = React.useState<string>(
    selectedOptionPrimary || ((optionsPrimary && optionsPrimary[0].value) ?? ""),
  );
  const [optionSelectSecondary, setOptionSelectSecondary] = React.useState<string>(
    selectedOptionSecondary || ((optionsSecondary && optionsSecondary[0].value) ?? ""),
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onQueryChange = React.useCallback(debounce(onChangeQuery, 500), [onChangeQuery]);
  const onQuery = (evnt: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(evnt.target.value);
    onQueryChange(evnt.target.value);
  };
  const onSelectOptionPrimary = (evnt: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setOptionSelect(evnt.target.value as string);
    if (onChangeOptionPrimary) onChangeOptionPrimary(evnt.target.value as string);
  };
  const onSelectOptionSecondary = (evnt: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setOptionSelectSecondary(evnt.target.value as string);
    if (onChangeOptionSecondary) onChangeOptionSecondary(evnt.target.value as string);
  };
  return (
    <Box display="flex" flexDirection="column" p={4} pb={1} width={1}>
      <Box flexGrow={1} width={1}>
        <TextField
          id="inspector-search"
          variant="outlined"
          placeholder={queryPlaceholderText ?? "Search"}
          size="small"
          value={search}
          onChange={onQuery}
          className={classes.search}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ paddingRight: "0px" }}>
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box display="flex" width={1} justifyContent="space-between" alignItems="center">
        {optionsPrimary && (
          <Box pl={4} display="flex" alignItems="center" flexShrink={1} style={{ maxWidth: "40%" }}>
            <FormControl className={classes.select}>
              <Select
                labelId="primary-label"
                id="primary-select"
                value={optionSelect}
                onChange={onSelectOptionPrimary}
                disableUnderline
              >
                {optionsPrimary &&
                  optionsPrimary.map((option) => (
                    <MenuItem key={option.name} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {optionsSecondary && (
          <Box pl={4} display="flex" alignItems="center" flexShrink={1} style={{ maxWidth: "40%" }}>
            <FormControl className={classes.select}>
              <Select
                labelId="secondary-label"
                id="secondary-select"
                value={optionSelectSecondary}
                onChange={onSelectOptionSecondary}
                disableUnderline
              >
                {optionsSecondary &&
                  optionsSecondary.map((option) => (
                    <MenuItem key={option.name} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box display="flex" justifyContent="space-evenly">
          {commands &&
            commands.map((command: ButtonCommand) => (
              <Tooltip key={`cmd-${command.title}`} title={command.title} TransitionComponent={Zoom} arrow>
                <IconButton aria-label={command.title} onClick={() => command.onAction()}>
                  {command.icon}
                </IconButton>
              </Tooltip>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SearchToolbar;
